import { doc, collection, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getBytes, deleteObject } from 'firebase/storage'
import { db, storage } from '@/firebase'
import {
  RULE_EXPECTATIONS,
  attempt,
  accessDetail,
  fmtExpected,
  probe
} from '../ruleExpectations.js'

// encrypted-pdfs/{studentId}/{file} shares canReadStudentFiles /
// canWriteStudentFiles with general/ and sensitive/. A missing student makes
// the Storage get() fail, so the owning path is skipped when this role cannot
// create a student — same honesty rule as documentsAccess.

export default {
  id: 'encryptedPdfs',
  label: 'Encrypted PDF storage',
  description:
    'Storage encrypted-pdfs/{studentId} upload/download for your role. Same relationship rules as student document folders.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'encrypted PDF write',
        'Select a school before probing school-scoped encrypted-pdfs'
      )
      return
    }

    const studentRef = doc(collection(db, 'students'))
    const fileRef = storageRef(storage, `encrypted-pdfs/${studentRef.id}/smoke-probe.txt`)
    const payload = new Blob([`smoke encrypted ${ctx.runId}`], { type: 'text/plain' })
    const expectCreate = RULE_EXPECTATIONS.studentCreate(role)
    const expectWrite = RULE_EXPECTATIONS.storageStudentWrite(role, {
      isCaseManagerOfStudent: true,
      inStaffIds: false
    })
    const expectRead = RULE_EXPECTATIONS.storageStudentRead(role, {
      isCaseManagerOfStudent: true,
      inStaffIds: false
    })
    let created = false

    try {
      await step(
        `create smoke student for encrypted PDF (expected: ${fmtExpected(expectCreate)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(studentRef, {
              app: {
                studentData: {
                  firstName: 'SMOKE',
                  lastName: 'EncPdf',
                  plan: 'IEP',
                  caseManagerId: ctx.user.uid
                },
                accommodations: { instruction: '', assessment: '' },
                schedule: { classServices: [] },
                staffIds: []
              },
              __smokeTest: true,
              __smokeRunId: ctx.runId,
              createdAt: serverTimestamp(),
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
          )
          created = allowed
          return allowed
            ? `${accessDetail(expectCreate, allowed)} — students/${studentRef.id}`
            : accessDetail(expectCreate, allowed)
        }
      )

      if (!created) {
        step.skip(
          'encrypted-pdfs upload',
          'Role cannot create a student, so encrypted-pdfs relationship checks cannot be probed from this account.'
        )
        return
      }

      await step(`upload to encrypted-pdfs (expected: ${fmtExpected(expectWrite)})`, () =>
        probe(expectWrite, () => uploadBytes(fileRef, payload), {
          onUnexpectedSuccess: () => deleteObject(fileRef).catch(() => {})
        })
      )

      if (expectWrite) {
        await step(
          `download from encrypted-pdfs (expected: ${fmtExpected(expectRead)})`,
          async () => {
            const { allowed } = await attempt(async () => {
              const bytes = await getBytes(fileRef)
              if (!bytes.byteLength) throw new Error('Downloaded file is empty')
            })
            return accessDetail(expectRead, allowed)
          }
        )
      } else {
        step.skip('download from encrypted-pdfs', 'Upload not expected — later steps skipped')
      }
    } finally {
      await deleteObject(fileRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
